import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, CheckIcon } from '@heroicons/react/24/outline';
import { IUserModel } from '@packages/global';

interface ModelSelectorProps {
  models: IUserModel[];
  selectedModelId: string;
  onChange: (modelId: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function ModelSelector({
  models,
  selectedModelId,
  onChange,
  loading = false,
  disabled = false,
  placeholder
}: ModelSelectorProps) {
  const { t } = useTranslation('common_ui');
  const resolvedPlaceholder = placeholder ?? t('modelSelector.placeholder');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedModel = models.find(m => m.id === selectedModelId);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (modelId: string) => {
    onChange(modelId);
    setIsOpen(false);
  };

  // 获取模型图标文字：中文取第一个字，英文取前三个字母
  const getModelIconText = (title: string): string => {
    const firstChar = title.charAt(0);
    // 检查是否是中文字符
    if (/[\u4e00-\u9fa5]/.test(firstChar)) {
      return firstChar;
    }
    // 英文取前三个字母，保持原文大小写
    return title.slice(0, 3);
  };

  // 获取模型标签（如 Audio、Turbo 等）
  const getModelTags = (model: IUserModel): string[] => {
    const tags: string[] = [];
    if (model.audioInputCount && model.audioInputCount > 0) tags.push('Audio');
    if (model.title.toLowerCase().includes('turbo')) tags.push('Turbo');
    if (model.title.includes('大师') || model.title.includes('Pro')) tags.push('Pro');
    return tags;
  };

  if (loading) {
    return (
      <div className="w-full border border-border-subtle rounded-lg px-3 py-2.5 bg-bg-elevated flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-400">{t('modelSelector.loading')}</span>
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <div className="w-full border border-border-subtle rounded-lg px-3 py-2.5 text-red-400 bg-bg-elevated">
        {t('modelSelector.empty')}
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* 触发按钮 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2.5 bg-bg-elevated transition-colors ${isOpen ? 'border-primary' : 'border-border-subtle hover:border-border-default'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-2">
          {selectedModel ? (
            <>
              <span className="text-gray-50">{selectedModel.title}</span>
              {getModelTags(selectedModel).map(tag => (
                <span
                  key={tag}
                  className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/20 text-primary rounded"
                >
                  {tag}
                </span>
              ))}
            </>
          ) : (
            <span className="text-gray-500">{resolvedPlaceholder}</span>
          )}
        </div>
        <ChevronUpIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* 下拉面板 */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-bg-surface border border-border-subtle rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="max-h-80 overflow-y-auto py-2">
            {models.map(model => {
              const isSelected = model.id === selectedModelId;
              const tags = getModelTags(model);

              return (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => handleSelect(model.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${isSelected ? 'bg-bg-elevated' : 'hover:bg-bg-elevated'
                    }`}
                >
                  {/* 模型图标 */}
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-bold text-sm">
                      {getModelIconText(model.title)}
                    </span>
                  </div>

                  {/* 模型信息 */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-50 font-medium truncate">{model.title}</span>
                      {tags.map(tag => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/20 text-primary rounded flex-shrink-0"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    {model.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{model.description}</p>
                    )}
                  </div>

                  {/* 选中标记 */}
                  {isSelected && (
                    <CheckIcon className="w-5 h-5 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
